import React from "react";

import { faExpand } from "@fortawesome/pro-regular-svg-icons";

import { ExecutedPromptMarkdown } from "src/aiNode/components/ExecutedPromptMarkdown";
import { ExecutedPromptWindowData } from "src/aiNode/components/ExecutedPromptWindow";
import {
  compilePromptTemplate,
  ExecutedPromptVariables,
} from "src/aiNode/executedPromptsUtils";
import { NodeExecutionMetadata } from "src/api/types";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { AiNodeV2ConversationMessageTextContentBlockBlockTypeEnum } from "src/clients/flow-api";
import {
  AiNodeV2,
  NodeHistoricalRunStateV2,
} from "src/constants/NodeDataTypes";
import { IconButton } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";

const getMessageTemplate = (
  message: AiNodeV2["data"]["prompts"]["messages"][number],
): string =>
  message.content
    .flatMap((block) =>
      block.block_type ===
      AiNodeV2ConversationMessageTextContentBlockBlockTypeEnum.TEXT
        ? [block.text.expression]
        : [],
    )
    .join("\n\n");

const PromptSection: React.FC<{
  title: string;
  copyValue: () => string;
  onOpenWindow: () => void;
  children: React.ReactNode;
}> = ({ title, copyValue, onOpenWindow, children }) => (
  <div className="flex flex-col gap-y-3 p-4" data-loc="executed-prompt-section">
    <div className="flex items-center justify-between">
      <span className="font-inter-semibold-14px text-gray-800">{title}</span>
      <div className="flex items-center gap-x-1">
        <Tooltip placement="top" title="Open in window" asChild>
          <IconButton
            dataLoc="open-executed-prompt-window"
            icon={faExpand}
            size="xs"
            onClick={onOpenWindow}
          />
        </Tooltip>
        <CopyTextIcon
          dataLoc="copy-executed-prompt"
          tooltip="Copy prompt"
          value={copyValue}
        />
      </div>
    </div>
    {children}
  </div>
);

const ExecutedPromptsContent: React.FC<{
  node: AiNodeV2;
  variables: ExecutedPromptVariables;
}> = ({ node, variables }) => {
  const { system_prompt: systemPrompt, messages } = node.data.prompts;
  const { open: openWindow } = useFloatingWindowsActions();

  const handleOpenWindow = (
    sectionKey: string,
    title: string,
    template: string,
  ) => {
    openWindow(
      {
        id: `executed-prompt-${node.id}-${sectionKey}`,
        type: "executed-prompt",
        target: undefined,
      },
      {
        title: `${node.data.label} — ${title}`,
        template,
        variables,
      } satisfies ExecutedPromptWindowData,
    );
  };

  return (
    <div
      className="divide-y divide-gray-200 rounded-lg border border-gray-200"
      data-loc="executed-prompts"
    >
      {systemPrompt.active && (
        <PromptSection
          copyValue={() =>
            compilePromptTemplate(systemPrompt.expression, variables)
          }
          title="System prompt"
          onOpenWindow={() =>
            handleOpenWindow(
              "system-prompt",
              "System prompt",
              systemPrompt.expression,
            )
          }
        >
          <ExecutedPromptMarkdown
            template={systemPrompt.expression}
            variables={variables}
          />
        </PromptSection>
      )}
      {messages.map((message, index) => (
        <PromptSection
          key={index}
          copyValue={() =>
            compilePromptTemplate(getMessageTemplate(message), variables)
          }
          title="Prompt"
          onOpenWindow={() =>
            handleOpenWindow(
              `message-${index}`,
              "Prompt",
              getMessageTemplate(message),
            )
          }
        >
          <div className="flex flex-col gap-y-2">
            {message.content.map((block, blockIndex) =>
              block.block_type ===
              AiNodeV2ConversationMessageTextContentBlockBlockTypeEnum.TEXT ? (
                <ExecutedPromptMarkdown
                  key={blockIndex}
                  template={block.text.expression}
                  variables={variables}
                />
              ) : (
                block.files.active && (
                  <span
                    key={blockIndex}
                    className="font-inter-normal-12px text-gray-500 italic"
                    data-loc="executed-prompt-files-note"
                  >
                    Attached files are not shown
                  </span>
                )
              ),
            )}
          </div>
        </PromptSection>
      ))}
    </div>
  );
};

type ExecutedPromptsDecisionHistoryTabProps = {
  node: AiNodeV2;
  runState: NodeHistoricalRunStateV2;
};

export const ExecutedPromptsDecisionHistoryTab: React.FC<
  ExecutedPromptsDecisionHistoryTabProps
> = ({ node, runState }) => (
  <div className="px-6 py-4">
    <ExecutedPromptsContent
      node={node}
      variables={runState.nodeExecutionMetadata?.[node.id]?.variables ?? {}}
    />
  </div>
);

type ExecutedPromptsTestRunTabProps = {
  node: AiNodeV2;
  nodeExecutionMetadata?: NodeExecutionMetadata;
  isFetching: boolean;
};

export const ExecutedPromptsTestRunTab: React.FC<
  ExecutedPromptsTestRunTabProps
> = ({ node, nodeExecutionMetadata, isFetching }) => {
  if (isFetching) {
    return (
      <div className="flex flex-col gap-y-3 pt-7">
        <Skeleton height="h-6" />
        <Skeleton height="h-6" width="w-3/4" />
        <Skeleton height="h-6" width="w-5/6" />
      </div>
    );
  }

  return (
    <div className="pt-2 pb-4">
      <ExecutedPromptsContent
        node={node}
        variables={nodeExecutionMetadata?.[node.id]?.variables ?? {}}
      />
    </div>
  );
};
